import React, { useState } from "react";
import styled from "styled-components";
import { IListing, INft } from "../../../commonTypes";
import useBidCard from "../useBidCard";
import {
  TrackSubtitle,
  TrackSubtitleContainer,
  TrackTitle,
} from "../sharedStyles";
import { useTranslation } from "react-i18next";
import Typography from "../../Typography/Typography";
import { formatDate, formatTime } from "../../../../helpers/dateTime";
import { getVerticalGap } from "../../../styles";
import Button from "../../Buttons/Button";
import ButtonOutlined from "../../Buttons/ButtonOutlined";
import DownloadModal from "./DownloadModal";
import { useWalletHelpers } from "../../../hooks/useWalletHelpers";
import { useNftOwnershipCheck } from "./useNftOwnershipCheck";
import { ListingStatusEnum } from "../../../commonEnums";
import Card from "../../Card/Card";
import LoadingSpinner from "../../LoadingSpinner";
import ShareBidButton from "../../ShareBidButton/ShareBidButton";
import { isListingMinting } from "../../../../helpers/listingHelper";

const Root = styled.div``;

const StyledCard = styled(Card)`
  padding: 20px;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const Sections = styled.div`
  ${Section}:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.darkBorderColor};
    margin-bottom: 16px;
    padding-bottom: 16px;
  }
`;

const Buttons = styled.div`
  margin-top: 32px;
  width: 100%;
  ${getVerticalGap("10px")};
`;

const DownloadInfo = styled.div`
  margin-top: 10px;
`;

const ConnectWallet = styled(Typography)`
  text-decoration: underline;
  cursor: pointer;
`;

const MintingLoader = styled(LoadingSpinner)`
  color: ${(props) => props.theme.accent.normal};
`;

interface IProps {
  listing: IListing;
  nft?: INft;
}

const SoldBidCard = ({ listing, nft }: IProps) => {
  const { connectWalletSafe } = useWalletHelpers();
  const { isWalletConnected, isOwner, isCheckingOwnership } =
    useNftOwnershipCheck(nft);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const { t } = useTranslation();
  const { title, price, priceUsd, endTime } = useBidCard(listing);

  const isMinting = isListingMinting(listing);

  const subtitle = isMinting ? t("listing-minting") : t("track-sold-info");

  return (
    <>
      <Root>
        <TrackTitle>{title}</TrackTitle>
        <TrackSubtitleContainer>
          <TrackSubtitle>{subtitle}</TrackSubtitle>
          {isMinting && <MintingLoader />}
        </TrackSubtitleContainer>
        <StyledCard>
          <Sections>
            <Section>
              <div>
                <Typography size="lg" secondary>
                  {t("sold-price")}
                </Typography>
                <Typography size="xl" weight="500">
                  {price} {listing.asset.symbol}
                </Typography>
              </div>
              <Typography size="xl" secondary>
                ${priceUsd}
              </Typography>
            </Section>
            <Section>
              <div>
                <Typography size="lg" secondary>
                  {t("sold-date")}
                </Typography>
                <Typography size="xl" weight="500">
                  {formatDate(endTime)}
                </Typography>
              </div>
              <Typography size="xl" secondary>
                {formatTime(endTime)}
              </Typography>
            </Section>
          </Sections>

          <Buttons>
            <ShareBidButton music={listing.music} listingId={listing.id} />
            {isOwner && (
              <Button
                onClick={() => setIsDownloadOpen(true)}
                text={t("download")}
                height="48px"
                fullWidth
              />
            )}
            {isCheckingOwnership && (
              <ButtonOutlined
                onClick={() => {}}
                text=""
                height="48px"
                fullWidth
                isLoading
              />
            )}
          </Buttons>
        </StyledCard>
        {!isWalletConnected && listing.status === ListingStatusEnum.minted && (
          <DownloadInfo>
            <Typography element="span" secondary size="sm">
              {t("ownership-verify-header")}
            </Typography>
            <div onClick={() => connectWalletSafe()}>
              <ConnectWallet element="span" secondary size="sm">
                {t("ownership-verify-action")}
              </ConnectWallet>
            </div>
          </DownloadInfo>
        )}
      </Root>
      {nft && isDownloadOpen && (
        <DownloadModal
          nft={nft}
          music={listing.music}
          isOpen={isDownloadOpen}
          onClose={() => setIsDownloadOpen(false)}
        />
      )}
    </>
  );
};

export default SoldBidCard;
